import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, useMantineTheme} from '@mantine/core';
import {useMediaQuery} from '@mantine/hooks';
import React, {useLayoutEffect, useRef} from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Rendered size of the animated success mark, in pixels. */
const SUCCESS_ICON_SIZE_PX = 32;

/** Geometry from the design spec (viewBox 0 0 300 300). */
const CIRCLE_CX = 150;
const CIRCLE_CY = 150;
const CIRCLE_R = 64.2;
const STROKE_WIDTH = 9;
/** Crop viewBox to the stroked circle bounds so nothing clips at 32px. */
const VIEWBOX_MIN = CIRCLE_CX - CIRCLE_R - STROKE_WIDTH / 2;
const VIEWBOX_SIZE = 2 * (CIRCLE_R + STROKE_WIDTH / 2);
const SUCCESS_ICON_VIEWBOX = `${VIEWBOX_MIN} ${VIEWBOX_MIN} ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`;

/** Circle stroke length for the draw-on animation (`2 * π * r`, r = 64.2). */
const CIRCLE_STROKE_LENGTH = 403;
/** Check stroke length for the draw-on animation. */
const CHECK_STROKE_LENGTH = 120;
const CIRCLE_DRAW_MS = 620;
const CHECK_DRAW_MS = 610;
const CHECK_DRAW_DELAY_MS = 330;
const DRAW_EASING = 'cubic-bezier(0.5, 0, 0, 1)';

const useStyles = createStyles(() => ({
  wrap: {
    flex: 'none',
    width: SUCCESS_ICON_SIZE_PX,
    height: SUCCESS_ICON_SIZE_PX,
    lineHeight: 0,
    overflow: 'visible',
  },
  svg: {
    display: 'block',
    width: SUCCESS_ICON_SIZE_PX,
    height: SUCCESS_ICON_SIZE_PX,
    overflow: 'visible',
  },
}));

/** Animated circle-and-check hero icon for the onboarding completion screen. */
export function OnboardingSuccessIcon(): JSX.Element {
  const theme = useMantineTheme();
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY) ?? false;
  const {classes} = useStyles();
  const stroke = theme.colors.blue[7];
  const circleRef = useRef<SVGCircleElement>(null);
  const checkRef = useRef<SVGPolylineElement>(null);

  useLayoutEffect(() => {
    const circle = circleRef.current;
    const check = checkRef.current;
    if (circle == null || check == null) {
      return undefined;
    }

    if (reducedMotion) {
      circle.style.strokeDashoffset = '0';
      check.style.strokeDashoffset = '0';
      return undefined;
    }

    // jsdom (unit tests) does not implement the Web Animations API.
    if (typeof circle.animate !== 'function') {
      circle.style.strokeDashoffset = '0';
      check.style.strokeDashoffset = '0';
      return undefined;
    }

    const circleAnimation = circle.animate(
      [{strokeDashoffset: `${CIRCLE_STROKE_LENGTH}`}, {strokeDashoffset: '0'}],
      {duration: CIRCLE_DRAW_MS, easing: DRAW_EASING, fill: 'forwards'}
    );
    const checkAnimation = check.animate(
      [{strokeDashoffset: `${CHECK_STROKE_LENGTH}`}, {strokeDashoffset: '0'}],
      {duration: CHECK_DRAW_MS, delay: CHECK_DRAW_DELAY_MS, easing: DRAW_EASING, fill: 'forwards'}
    );

    return () => {
      circleAnimation.cancel();
      checkAnimation.cancel();
    };
  }, [reducedMotion]);

  return (
    <Box className={classes.wrap} {...getTestProps({component: 'OnboardingSuccessIcon', element: 'root'})}>
      <svg
        className={classes.svg}
        viewBox={SUCCESS_ICON_VIEWBOX}
        width={SUCCESS_ICON_SIZE_PX}
        height={SUCCESS_ICON_SIZE_PX}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Success"
      >
        <circle
          ref={circleRef}
          cx={CIRCLE_CX}
          cy={CIRCLE_CY}
          r={CIRCLE_R}
          fill="none"
          stroke={stroke}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={CIRCLE_STROKE_LENGTH}
          strokeDashoffset={reducedMotion ? 0 : CIRCLE_STROKE_LENGTH}
        />
        <polyline
          ref={checkRef}
          points="116,150 140,174 184,126"
          fill="none"
          stroke={stroke}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={CHECK_STROKE_LENGTH}
          strokeDashoffset={reducedMotion ? 0 : CHECK_STROKE_LENGTH}
        />
      </svg>
    </Box>
  );
}
